import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, fetchData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { Moment, MomentResponse, SpeechText, ChannelItem } from '../types/api';
import { is_editable } from '../utils/formUtils';
import { useLayout } from '../contexts/LayoutContext';
import '../styles/screens/MomentView.css';

const MomentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { updateHeaderProps } = useLayout();
  const [moment, setMoment] = useState<Moment | null>(null);
  const [channels, setChannels] = useState<ChannelItem[]>([]);
  const [speechTexts, setSpeechTexts] = useState<SpeechText[]>([]);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    updateHeaderProps({
      pageTitle: 'View Moment',
      showInteractionExId: false,
    });
  }, []);

  useEffect(() => {
    if (dataLoaded || !id) return;

    const loadMoment = async () => {
      try {
        const response = await fetchData<MomentResponse>(api(PATH_SEGMENTS.MOMENT, id));
        setMoment(response.moment);
        
        // Load channels
        const channelsResponse = await fetchData<{channels: ChannelItem[]}>(api(PATH_SEGMENTS.CHANNEL));
        setChannels(channelsResponse.channels || []);
        
        setDataLoaded(true);
      } catch (error) {
        console.error("Error loading moment:", error);
        setDataLoaded(true);
      }
    };

    loadMoment();
  }, [id, dataLoaded]);

  const handleSearch = async () => {
    if (id) {
      try {
        const response = await fetchData<{ speech_texts: SpeechText[] }>(
          `${api(PATH_SEGMENTS.SPEECHTEXT)}?mmt_id=${id}`
        );
        console.log(response);
        setSpeechTexts(response.speech_texts || []);
        setSearchPerformed(true);
      } catch (error) {
        console.error("Error searching for speech texts:", error);
      }
    }
  };

  const handleEdit = () => {
    if (id && moment && is_editable(moment.state_id)) {
      navigate(`/moment/edit/${id}`);
    }
  };

  const handleCopy = () => {
    if (moment) {
      navigate('/moment/new', { 
        state: { 
          copyFrom: moment,
          isCopy: true
        } 
      });
    }
  };

  const handleCreateNew = () => {
    navigate('/moment/new');
  };

  // Format examples (split by semicolon)
  const formatExamples = (examples: string | null) => {
    if (!examples) return [];
    return examples.split(';').map(example => example.trim()).filter(Boolean);
  };

  // Helper function to extract channel IDs
  const getSelectedChannelIds = (): number[] => {
    if (!moment) return [];
    return moment.channels?.map(ch => ch.channel_id) || [];
  };

  if (!moment) {
    return <div className="loading">Loading moment...</div>;
  }

  return (
    <div className="moment-view-container">
      <div className="moment-content">
        <div className="field-row">
        <span className="field-label">Name</span>
          <div className="name-value">{moment.name}</div>
          <div className="moment-actions">
            <button className="action-button edit-button" onClick={handleEdit} disabled={!moment || !is_editable(moment.state_id)}>
              Edit
            </button>
            <button className="action-button copy-button" onClick={handleCopy}>
              Create a Copy
            </button>
            <button className="action-button new-button" onClick={handleCreateNew}>
              Create New
            </button>
          </div>
        </div>
        <div className="field-row">
          <span className="field-label">GenAI Prompt</span>
          <div className="prompt-container">
            <div className="prompt-text">{moment.prompt}</div>
            <button className="action-button" onClick={handleSearch}>Search</button>
          </div>
        </div>
        
        {moment.examples && (
          <div className="examples-section">
            <span className="examples-label">Examples:</span>
            <div className="examples-container">
              {formatExamples(moment.examples).map((example, index) => (
                <div key={index} className="example-chip">{example}</div>
              ))}
            </div>
          </div>
        )}
        
        <div className="field-row">
          <span className="field-label">Language</span>
          <div className="language-options">
            <div className="radio-option">
              <input 
                type="radio" 
                id="english" 
                name="language" 
                checked={moment.language_id === 1} 
                readOnly 
              />
              <label htmlFor="english">English</label>
            </div>
            <div className="radio-option">
              <input 
                type="radio" 
                id="spanish" 
                name="language" 
                checked={moment.language_id === 2} 
                readOnly 
              />
              <label htmlFor="spanish">Spanish</label>
            </div>
          </div>
        </div>
        
        <div className="field-row">
          <span className="field-label">Sentiments</span>
          <div className="dropdown-container">
            <select disabled value={moment.sentiment_id}>
              <option>Neutral</option>
            </select>
          </div>
        </div>
        
        <div className="field-row">
          <span className="field-label">Channel</span>
          <div className="channel-options">
            {channels.map(channel => (
              <div key={channel.id} className="checkbox-option">
                <input 
                  type="checkbox" 
                  id={`channel-${channel.id}`}
                  checked={getSelectedChannelIds().includes(channel.id)}
                  readOnly 
                />
                <label htmlFor={`channel-${channel.id}`}>{channel.name}</label>
              </div>
            ))}
          </div>
        </div>
        
        <div className="field-row">
          <span className="field-label">Type</span>
          <div className="dropdown-container">
            <select disabled value={moment.occurrence_id}>
              <option>Present</option>
            </select>
          </div>
        </div>
        
        <div className="field-row">
          <span className="field-label">Speaker</span>
          <div className="dropdown-container">
            <select disabled value={moment.speaker_id}>
              <option>Agent</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="search-results-section">
        <div className="search-results-header">
          <span className="field-label">Search Results</span>
          <div className="search-count">{speechTexts.length > 0 ? `${speechTexts.length} occurrences in ${Math.floor(speechTexts.length / 2)} analyzed interactions` : ''}</div>
        </div>
        
        {searchPerformed && speechTexts.length > 0 && (
          <div className="search-results-list">
            {speechTexts.map((speech, index) => (
              <div key={index} className="result-item">
                <div className="result-header">
                  <span className="speaker-name">{speech.speaker_name}</span>
                </div>
                <div className={`speech-text ${speech.speaker_name.toLowerCase() === 'agent' ? 'agent-speech' : 'customer-speech'}`}>
                  {speech.speech}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {searchPerformed && speechTexts.length === 0 && (
          <div className="no-results">No results found</div>
        )}
      </div>
    </div>
  );
};

export default MomentView; 